import React from 'react';
import { Button, SelectLabel, Modal, Card, ToggleCard, toast } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import MailTemplate from '../../models/mailTemplate';
import { fetcher } from '@weezevent/weezjs-core';
import Loading from '../../components/loading';
import { ImageBackground } from '../../components/template-select/templateItem';
import { useNotifications, Notification } from '../../components/notifications/notificationsProvider';
import Style from './styles/partnerList.module.css';

export default function TemplateSelectionModal({ showModal = false, setShowModal = () => {}, organizationId = null, selectedPartners = [], setSelectedPartners = () => {} }) {
    const { t } = useTranslation();

    const { addNotification } = useNotifications();

    const [loading, setLoading] = React.useState(false);
    const [ticketingPartner, setTicketingPartner] = React.useState(false);
    const [ticketingPartnerUserLink, setTicketingPartnerUserLink] = React.useState(false);
    const [rules, setRules] = React.useState({});

    const defaultTempalteRule = {
        ticketing_partner: 3468,
        ticketing_partner_user_link: 3439
    };

    const [templates, loadingTemplates] = MailTemplate.useApiModel(
        {
            organizationId: organizationId,
            key: 'crm-mail'
        },
        [organizationId]
    );

    const selectedTemplateUrl = React.useCallback(
        type => {
            return templates?.find(template => template.id === rules[type])?.preview_url;
        },
        [rules, templates]
    );

    const selectOptions = React.useMemo(() => {
        if (!templates) {
            return [];
        }
        return templates.map(option => {
            return {
                key: option.id,
                value: option.id,
                text: option.name
            };
        });
    }, [templates]);

    const _selectOptions = React.useCallback(
        type => {
            let options = [...selectOptions];
            options.unshift({
                key: defaultTempalteRule[type],
                value: defaultTempalteRule[type],
                text: t('ticket.partnersList.template-modal.dropdown-invitation.default-template')
            });
            return options;
        },
        [t, selectOptions]
    );

    const onToggle = React.useCallback(
        (type, checked) => {
            if (!checked) {
                const { [type]: _type, ..._rules } = rules;
                setRules(_rules);
            } else {
                if (type === 'ticketing_partner') {
                    setTicketingPartner(checked);
                } else {
                    setTicketingPartnerUserLink(checked);
                }
                setRules({ ...rules, [type]: defaultTempalteRule[type] });
            }
        },
        [rules]
    );

    const onClose = React.useCallback(() => {
        setShowModal(false);
        setTicketingPartner(false);
        setTicketingPartnerUserLink(false);
        setSelectedPartners({});
    }, []);

    const isDisabled = React.useMemo(() => {
        return !Object.keys(rules).length;
    }, [rules]);

    const handleSubmit = React.useCallback(() => {
        setLoading(true);
        const data = {
            type: 'manage_template_rules',
            config: {
                rules,
                partner_ids: Object.keys(selectedPartners)
            }
        };
        fetcher
            .post(`/ticket/organizations/${organizationId}/partners/mailing/actions`, { data: data })
            .then(response => {
                addNotification(new Notification('async_actions', 'manage_template_rules', 'partners', response?.response?.task_id, 'ticket.partnersList.template-modal.toast-'));
                toast.info(t('ticket.manager.attendees.action.generic.toast.async'));
            })
            .catch(() => {
                toast.error(t('ticket.partnersList.template-modal.toast-error'));
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    }, [t, rules, selectedPartners, organizationId, onClose]);

    if (loadingTemplates) {
        return <Loading />;
    }

    return (
        <Modal
            size="large"
            allowExternalClosing={true}
            open={showModal}
            onClose={onClose}
            title={t('ticket.partnersList.template-modal.title')}
            subtitle={t('ticket.partnersList.template-modal.subtitle')}
            cancelButton={<Button onClick={onClose} label={t('ticket.common.action.close')} />}
            submitButton={<Button primary disabled={isDisabled} isLoading={loading} onClick={handleSubmit} label={t('ticket.common.action.save')} />}
        >
            <>
                <Card>
                    <ToggleCard
                        title={t('ticket.partnersList.template-modal.toggle-email-invitation.title')}
                        subtitle={t('ticket.partnersList.template-modal.toggle-email-invitation.description')}
                        toggle={ticketingPartner}
                        onToggle={checked => onToggle('ticketing_partner', checked)}
                    >
                        <SelectLabel
                            useNew
                            value={rules.ticketing_partner}
                            label={t('ticket.partnersList.template-modal.dropdown-invitation.title')}
                            options={_selectOptions('ticketing_partner')}
                            onChange={value => setRules({ ...rules, ticketing_partner: value })}
                        />
                        {rules.ticketing_partner !== 3468 && <ImageBackground className={Style['image-preview']} previewUrl={selectedTemplateUrl('ticketing_partner')} />}
                    </ToggleCard>
                </Card>
                <Card>
                    <ToggleCard
                        title={t('ticket.partnersList.template-modal.toggle-email-user.title')}
                        subtitle={t('ticket.partnersList.template-modal.toggle-email-user.description')}
                        toggle={ticketingPartnerUserLink}
                        onToggle={checked => onToggle('ticketing_partner_user_link', checked)}
                    >
                        <SelectLabel
                            useNew
                            value={rules.ticketing_partner_user_link}
                            label={t('ticket.partnersList.template-modal.dropdown-user.title')}
                            options={_selectOptions('ticketing_partner_user_link')}
                            onChange={value => setRules({ ...rules, ticketing_partner_user_link: value })}
                        />
                        {rules.ticketing_partner_user_link !== 3439 && (
                            <ImageBackground className={Style['image-preview']} previewUrl={selectedTemplateUrl('ticketing_partner_user_link')} />
                        )}
                    </ToggleCard>
                </Card>
            </>
        </Modal>
    );
}
